import { useState, useEffect } from "react";
import to from "await-to-js";
import cn from "clsx";
import { Checkbox, DialogContent, DialogTitle } from "@mui/material";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import JDIPrice from "@/common-components-src/js/price/index";
import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKCounter from "@/common-components-src/components/UKCounter";
import UKImg from "@/common-components-src/components/UKImg";
import UKLoadingIcon from "@/common-components-src/components/UKLoadingIcon";
import { useStores } from "@/app/csr-src/models";
import RuleWrap from "../ruleWrap";
import CommonButton from "../commonButton";
import EmptyPage from "../emptyPage";
import ErrorPage from "../errorPage";
import ProductTag from "../productTag";
import styles from "./index.module.scss";
import { fetchRecordApplyInfo } from "@/app/csr-src/apis/afterSales/afterSales.api";
import { IRecordApplyInfoReq, SkuListVo } from "@/app/csr-src/apis/afterSales/afterSales.interface";
import TipIcon from "@/app/csr-src/assets/icons/TipIcon.svg";

interface returnProductModalProps {
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  handleConfirmClick: () => void;
  skuId?: number;
  skuList: {
    skuId: string;
    skuUUid: string;
  }[];
}

const returnProductModal: React.FC<returnProductModalProps> = observer(({ orderId, isOpen, closeHandler, handleConfirmClick, skuId, skuList }) => {
  const { t } = useTranslation();
  const { AfterSalesStore } = useStores();

  const [wareInfoList, setWareInfoList] = useState<SkuListVo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadProductList = async () => {
    const requestData: IRecordApplyInfoReq = { orderId: String(orderId), skuInfoList: skuList };
    const [err, wareInfoListData] = await to(fetchRecordApplyInfo(requestData));

    if (err) {
      setError("Failed to fetch order details");
      setWareInfoList([]);
    } else {
      const wareInfoDTOList = wareInfoListData?.skuList || [];
      // 添加 activeCheck 属性
      let updatedWareInfoDTOList = wareInfoDTOList.map((item) => ({
        ...item,
        // applyCount: skuId && item.skuId === skuId ? 1 : Number(item.canApplyCount), // 选中的售后商品数量
        applyCount: skuId ? 1 : Number(item.canApplyCount),
        // 没有skuId默认是选中全部，有skuId选中的是当前sku
        activeCheck: !skuId ? true : item.skuId === skuId ? true : false, // 默认选中点击的sku
      }));
      // updatedWareInfoDTOList
      const itemToMove = updatedWareInfoDTOList.find((item) => item.skuId === skuId);
      if (itemToMove) {
        updatedWareInfoDTOList = [itemToMove, ...updatedWareInfoDTOList.filter((item) => item.skuId !== skuId)];
      }
      // 前端过滤展示可申请售后的商品列表
      const validList = updatedWareInfoDTOList.filter((item) => item.canApply);
      setWareInfoList(validList);
      setError(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadProductList();
  }, []);

  // const {
  //   invoiceInfoDTOList,
  // } = logisticsList || {};

  // const invoiceInfoDTOList: {
  //   invoiceItem: {
  //     label: string;
  //     value: string;
  //     showCopyButton?: boolean;
  //     copyButtonTitle?: string;
  //   }[];
  // }[] = [];

  // 点击单个checkbox
  const handleItemCheck = (item: SkuListVo) => {
    setWareInfoList((prevList) => prevList.map((ele) => (ele.skuId === item.skuId ? { ...ele, activeCheck: !ele.activeCheck } : ele)));
  };

  // 全选功能
  const handleSelectAll = () => {
    const isAllSelectStatus = isSelectAll();
    setWareInfoList((prevList) =>
      prevList.map((ele) => {
        return { ...ele, activeCheck: !isAllSelectStatus };
      }),
    );
  };

  // 是否已经全部选中
  const isSelectAll = () => {
    return wareInfoList.every((item) => item.activeCheck);
  };

  // 已选择的数量
  const selectList = () => {
    return wareInfoList.filter((item) => item.activeCheck);
  };

  const isBtnEnable = () => {
    const selectedList = selectList();
    let enable = false;
    if (selectedList?.length) {
      enable = true;
    }
    return enable;
  };

  const confirmHandler = () => {
    const selectedList = selectList();
    if (selectedList?.length) {
      AfterSalesStore.setSelectedProductList(selectedList);
      handleConfirmClick();
    } else {
      UKToast.show({
        content: t("order.orderDetail.returnProductModal.errorTips"), // 弹窗内容
        icon: "warning", // 可选：success, fail, loading, warn, info，影响图标
      });
    }
  };

  return (
    <UKDialog open={isOpen} onClose={closeHandler} showCloseBtn={true} className={styles.refund_product_modal}>
      <DialogTitle>{t("order.orderDetail.returnProductModal.modalTitle")}</DialogTitle>
      <RuleWrap />
      <DialogContent>
        {loading ? (
          <div className={styles.loading_wrap}>
            <UKLoadingIcon type="black" />
            <span className={styles.loading_text}>{t("common.pageStatus.loading")}</span>
          </div>
        ) : error ? (
          <ErrorPage />
        ) : wareInfoList?.length ? (
          <div className={styles.return_wrap}>
            <div className={styles.select_all_wrap} onClick={handleSelectAll}>
              <Checkbox
                edge="start"
                disableRipple
                sx={{
                  color: "#E0E1E5",
                  "&.Mui-checked": {
                    color: "#CC0C1C",
                  },
                }}
                checked={isSelectAll() || false}
              />
              <span className={styles.select_label}>{t("order.orderDetail.returnProductModal.selectAllText")}</span>
              {!!selectList()?.length && (
                <span className={styles.select_num}>
                  {selectList()?.length} {t("order.orderDetail.returnProductModal.itemsText")}
                </span>
              )}
            </div>
            <ul
              className={cn(styles.ware_list_wrap, {
                [styles.ware_list_wrap_large]: wareInfoList?.length > 4,
              })}
            >
              {wareInfoList.map((item, index) => {
                return (
                  <li key={index} className={styles.ware_item_wrap}>
                    <Checkbox
                      edge="start"
                      checked={item?.activeCheck}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": String(item?.skuId) }}
                      sx={{
                        color: "#E0E1E5",
                        "&.Mui-checked": {
                          color: "#CC0C1C",
                        },
                      }}
                      onClick={() => handleItemCheck(item)}
                    />
                    <div className={styles.product_img}>
                      <UKImg
                        urlExtend={{
                          imgKey: item.skuImg ? item.skuImg : "",
                          width: 200,
                          height: 200,
                        }}
                      />
                    </div>
                    <div className={styles.product_cont}>
                      <div className={styles.productNameWrap}>
                        <div className={styles.tagsWrap}>
                          <ProductTag tagList={item?.showTagList || []} />
                        </div>
                        <div className={styles.product_name_cont}>{item.skuName}</div>
                      </div>
                      <div className={styles.product_desc_wrap}>
                        <div>
                          <div className={styles.product_property}>{item.propertyExt}</div>
                          {Number(item.canApplyCount) > 1 ? (
                            <div className={styles.counter_wrap}>
                              <UKCounter
                                immediately={false}
                                value={
                                  // skuId && item.skuId === skuId ? 1 : Number(item.canApplyCount)
                                  skuId ? 1 : Number(item.canApplyCount)
                                }
                                minAmount={1}
                                maxAmount={Number(item.canApplyCount)}
                                stock={Number(item.canApplyCount)}
                                onChange={(num: number) => {
                                  item.applyCount = num;
                                  console.log("add cart num 数量", num);
                                }}
                                size="small"
                              />
                            </div>
                          ) : item.canApplyCount ? (
                            <div className={styles.product_num}>×{item.canApplyCount}</div>
                          ) : null}
                        </div>
                        <div className={styles.price_wrap}>
                          {/* 到手价 */}
                          <JDIPrice
                            symbol={item.feeCurrencySymbol || ""}
                            price={item.skuSingleShouldPrice || ""}
                            uiType={1}
                            containerStyle={{
                              fontSize: "16px",
                              lineHeight: "20px",
                            }}
                            integerStyle={{
                              fontSize: "24px",
                              lineHeight: "24px",
                              height: "24px",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <EmptyPage />
        )}
        <div className={styles.oper_wrap}>
          <CommonButton
            variant="outlined"
            onClick={closeHandler}
            style={{
              marginRight: "12px",
            }}
          >
            {t("order.orderList.common.cancelBtnText")}
          </CommonButton>
          <CommonButton variant="contained" onClick={confirmHandler} disabled={!isBtnEnable()}>
            {t("checkout.common.continue")}
          </CommonButton>
        </div>
      </DialogContent>
    </UKDialog>
  );
});

export default returnProductModal;
